import React, { FC, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import api from '../../../api';
import { ReactComponent as ArrowRightIcon } from '../../../icons/arrow-right.svg';
import './thread-share-modal.scss';

interface User {
  id: string;
  name: string;
  currentPermission?: 'NONE' | 'READ' | 'WRITE' | 'ADMIN' | 'OWNER';
}

interface ThreadShareModalProps {
  threadId: string;
  isOwner: boolean;
  isViaCompany: boolean;
  sharePermission?: string;
  /** The actual company-wide permission level (READ, WRITE) when isViaCompany is true */
  companyPermission?: string;
  isMainThread?: boolean;
  isInternalMonologue?: boolean;
  onClose: () => void;
  /** Variant for different use cases */
  variant?: 'default' | 'newThread';
  /** For newThread variant - navigate to thread */
  agentId?: string;
}

const ThreadShareModal: FC<ThreadShareModalProps> = ({
  threadId,
  isOwner,
  isViaCompany,
  sharePermission,
  companyPermission,
  isMainThread,
  isInternalMonologue,
  onClose,
  variant = 'default',
  agentId,
}) => {
  const navigate = useNavigate();
  const { getAccessTokenSilently } = useAuth0();
  const [companyUsers, setCompanyUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [saving, setSaving] = useState<boolean>(false);

  useEffect(() => {
    if (
      variant === 'newThread' ||
      ((isOwner || (sharePermission === 'ADMIN' && !isViaCompany)) &&
        !isMainThread &&
        !isInternalMonologue)
    ) {
      fetchCompanyUsers();
    }
  }, [
    isOwner,
    sharePermission,
    isViaCompany,
    isMainThread,
    isInternalMonologue,
    variant,
  ]);

  const fetchCompanyUsers = async () => {
    try {
      setLoading(true);
      const token = await getAccessTokenSilently();
      const response = await api.get(`/agents/users/company`, {
        params: { thread_id: threadId },
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.data.success) {
        setCompanyUsers(response.data.users);
      }
    } catch (error) {
      console.error('Error fetching company users:', error);
    } finally {
      setLoading(false);
    }
  };

  const handlePermissionChange = async (userId: string, permission: string) => {
    const updatedUsers = companyUsers.map((user) =>
      user.id === userId
        ? { ...user, currentPermission: permission as any }
        : user,
    );
    setCompanyUsers(updatedUsers);

    // For newThread variant, save immediately on change
    if (variant === 'newThread') {
      try {
        const token = await getAccessTokenSilently();
        const shares = updatedUsers
          .filter(
            (user) =>
              user.currentPermission && user.currentPermission !== 'NONE',
          )
          .map((user) => ({
            user_id: user.id,
            permission_level: user.currentPermission!,
          }));

        await api.put(
          `/agents/threads/${threadId}/shares`,
          { shares },
          { headers: { Authorization: `Bearer ${token}` } },
        );
      } catch (error) {
        console.error('Error updating share:', error);
      }
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const token = await getAccessTokenSilently();

      // Build shares array from users with non-NONE permissions
      const shares = companyUsers
        .filter(
          (user) => user.currentPermission && user.currentPermission !== 'NONE',
        )
        .map((user) => ({
          user_id: user.id,
          permission_level: user.currentPermission!,
        }));

      await api.put(
        `/agents/threads/${threadId}/shares`,
        { shares },
        { headers: { Authorization: `Bearer ${token}` } },
      );

      onClose();
    } catch (error) {
      console.error('Error updating shares:', error);
    } finally {
      setSaving(false);
    }
  };

  const getPermissionDescription = (permission?: string) => {
    const perm = permission || sharePermission;
    if (perm === 'READ') {
      return 'read what is posted';
    } else if (perm === 'WRITE') {
      return 'speak with the agent directly';
    } else if (perm === 'ADMIN') {
      return 'add others and manage permissions';
    }
    return '';
  };

  const handleGoToChat = () => {
    onClose();
    if (agentId) {
      navigate(
        `/conversations?agent_id=${agentId}&thread_id=${threadId}&new_chat=true`,
      );
    }
  };

  return (
    <div
      className={`thread-share-modal ${variant === 'newThread' ? 'new-thread-variant' : ''}`}
    >
      {variant === 'newThread' ? (
        // New thread share view
        <>
          <p>People you share with will be able to see all your messages.</p>

          <div className='share-area'>
            {loading ? (
              <div className='loading'>Loading users...</div>
            ) : (
              <div className='users-list'>
                {companyUsers.map((user) => (
                  <div key={user.id} className='user-row'>
                    <div className='user-info'>
                      <div className='user-name'>{user.name}</div>
                    </div>
                    <select
                      value={user.currentPermission || 'NONE'}
                      onChange={(e) =>
                        handlePermissionChange(user.id, e.target.value)
                      }
                      className='permission-select'
                    >
                      <option value='NONE'>Not shared</option>
                      <option value='READ'>Read</option>
                      <option value='WRITE'>Write</option>
                      <option value='ADMIN'>Admin</option>
                    </select>
                  </div>
                ))}
              </div>
            )}
          </div>

          <button className='go-to-chat-btn' onClick={handleGoToChat}>
            Go to your chat
            <ArrowRightIcon />
          </button>
        </>
      ) : isMainThread || isInternalMonologue ? (
        // Cannot share view
        <>
          <h1>Cannot be shared</h1>
          <p>
            This thread cannot be shared as it is a special thread used by your
            Dreamie for specific actions.
          </p>
          <div className='modal-actions'>
            <button onClick={onClose} className='save-btn'>
              Done
            </button>
          </div>
        </>
      ) : isViaCompany ? (
        // Company-wide access view - applies to everyone including owner
        <>
          <h1>Visible to everyone</h1>
          <p>
            This conversation has company-wide{' '}
            {companyPermission?.toLowerCase() || 'read'} access. Everyone in
            your company can {getPermissionDescription(companyPermission)}.
          </p>
          <div className='modal-actions'>
            <button onClick={onClose} className='save-btn'>
              Done
            </button>
          </div>
        </>
      ) : !isOwner && sharePermission && sharePermission !== 'ADMIN' ? (
        // Explicit share view
        <>
          <h1>Shared with me</h1>
          <p>
            You have {sharePermission.toLowerCase()} access which means you can{' '}
            {getPermissionDescription()}.
          </p>
          <div className='modal-actions'>
            <button onClick={onClose} className='save-btn'>
              Done
            </button>
          </div>
        </>
      ) : (
        // Owner/Admin view
        <>
          <h1>Share conversation</h1>
          <p>
            The person you're sharing with will be able to see all your
            messages.
          </p>

          <div className='share-area'>
            {loading ? (
              <div className='loading'>Loading users...</div>
            ) : (
              <div className='users-list'>
                {companyUsers.map((user) => (
                  <div key={user.id} className='user-row'>
                    <div className='user-info'>
                      <div className='user-name'>{user.name}</div>
                    </div>
                    <select
                      value={user.currentPermission || 'NONE'}
                      onChange={(e) =>
                        handlePermissionChange(user.id, e.target.value)
                      }
                      className='permission-select'
                      disabled={user.currentPermission === 'OWNER'}
                    >
                      <option value='NONE'>None</option>
                      <option value='READ'>Read</option>
                      <option value='WRITE'>Write</option>
                      <option value='ADMIN'>Admin</option>
                    </select>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className='modal-actions'>
            <button onClick={onClose} className='cancel-btn' disabled={saving}>
              Cancel
            </button>
            <button onClick={handleSave} className='save-btn' disabled={saving}>
              {saving ? 'Saving...' : 'Save Permissions'}
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default ThreadShareModal;
